'use strict';

import TWEEN from './libs/tween.umd';
import { Clock, Vector2 } from 'three';
import {CSS2DRenderer} from "./libs/CSS2DRenderer.js";
import {
    createHtmlElement,
    getUrlParam,
    lockScreenForTablet,
    userPrevent,
    findSelectionAndCrmData,
    flatClick,
    detectMobile
} from './functions';
import {Buildings, isDev, isMobile, isSafari, isTablet, projectMode, settingsData, threeJsVariables} from './settings';
import {createThreeJsEnvironment, setStats} from "./modules/threejs/threejs-components";
import {getVars} from "./modules/customStyles/customStyles";
import {isDesktopMode, langSwitcher} from "./modules/language/langSwitcher";
import {RaycastHandler} from "./modules/controls/mouse-n-touches";
import {moveElements, rightNavigationBar, rightNavMenuZIndexChange} from "./modules/navigation/right-nav-bar";
import {initProjectMainScript} from './modules/threejs/autoProjectScript';
import { fitModelInViewport, setZoomOnWindowResize } from './modules/controls/zoomControl';
import {
    autoRotateInit,
    autoRotateTimerAction,
    debugOrbitControls,
    orbitControlsInit,
} from './modules/controls/orbitControls';
import {
    reopenDataBlockAfterResize,
    setWidthOfCardDependingOnMenu
} from "./modules/cards/optionsCards";
import {setSizesToFloorSlider} from "./modules/floorSlider/floorSlider";
import {buildingsLabelResize} from './modules/building-switch/buildingLabels';
import {leftNavigationBar} from "./modules/navigation/left-nav-bar";
import {CardTypes, ViewMode} from "./common";
import {getCurrentLangDirection, setInitialHtmlDirection} from './modules/language/langSwitcher';
import {animateCanvasResize} from './modules/threejs/animation';
import {setMinAndMaxControlsDistance} from './modules/controls/orbitControls';
import { additionalMenuToggle, menuInit, resizeMenu } from './modules/menu/menu';
import {bottomNavBarInit} from './modules/navigation/bottom-nav-bar';
import {compassInit, compassPositionChange} from './modules/compass/compass';
import {zoomButtonsControl} from './modules/controls/zoomControl';
import {
    checkResourcesLoading,
    loaderLineProgress,
    preloaderInit,
    preloaderSliderInit
} from './modules/preloader/preloader';
import {audioInit} from './modules/audio/audio';
import {widgetsInit} from './modules/widgets/widgets';
import {
    addPoiWidgetBasicStyle,
    panoramaOrbitResize,
    poiOrientationHandler,
} from './modules/widgets/poi/poiv3';
import {initForm} from "./modules/form/form";
import {addGui} from "./modules/gui/gui";
import { trackballControlsInit } from './modules/controls/trackballControls';
import {cameraTour, updateCameraTour} from "./modules/camera-auto-tour/cameraTour";
import {
    openSearchHandler,
    removeSearchContainer,
    searchResizeCanvas,
    updateComponentsOnCanvasResize,
} from './modules/search/search';
import { cssPoiHandler, initPoiData, resizeOrbitPoiCard } from './modules/poi/poiCss';
import {renderLoop} from "./modules/threejs/environment";
import {createStreetNames} from "./modules/street-names/addStreetNames";
import { addPoweredLogo } from './modules/poweredLogo/poweredLogo';
import { flatLabelResize } from './modules/flatLabels/flatLabels';
import {checkIntersection, selectionBoxesSetStatusColor} from "./modules/threejs/raycast";
import {createModal} from "./modules/cards/urlParamModal";
import {setWidthOfApplyNow} from "./modules/apply-now/applyNow";

import '../scss/main-style.scss';

import {getGoogleAnalyticsData} from "./modules/analytics/statistic";
import {UnitCardClass} from "./modules/cards/unitCards/UnitCardClass";
import { chatsInit } from './modules/chats/chats';
import { buildingHoverInit } from './modules/building-switch/buildingHover';
import { set } from 'lord-icon-element/build/helpers/utils';
import gsap from "gsap";
import { getContactsDataFromAPI } from './modules/contactUs/contactUs';
import { getLordIconColors } from './lordicon';
import {update as cameraUpdate, render as cameraRender, flyCameraAnimationLoop, cameraAnimationActive} from "./modules/threejs/camera";
import { loadCylinderGrid } from './modules/cylinderGrid/cylinderGrid';
import { showOrHideCylinderFloorNumbers } from './modules/cylinderFloorNumbers/cylinderFloorNumbers';
import {
    postProcessingRender
} from "./modules/post-processing/post-processing";
import {Pagination} from "./modules/pagination/Pagination";
import {Tooltip} from "./modules/tooltip/Tooltip";
import { Weather } from './modules/weather/Weather';
import { Snow } from './modules/weather/Snow';
import { PostFX } from './modules/post-processing/PostFX';

const MobileDetect = require('mobile-detect');

// iPhone's bottom black bar fix
if (isSafari) document.body.style.height = '100vh';


projectMode.Pagination = new Pagination();
addDocumentProperties();
addGui();
addPoiWidgetBasicStyle();
//preloaderInit();
preloaderSliderInit();
createThreeJsEnvironment();
templateResizeFunctionDuringProjectIsLoading();
const controls = orbitControlsInit();
const trackballControls = trackballControlsInit();
// cloudsInit();
initProjectMainScript();
setStats();

const clock = new Clock();
let elapsedTime = 0;

function templateResizeFunctionDuringProjectIsLoading() {
    window.addEventListener('resize', onLoadWindowResize, false);
    window.addEventListener('orientationchange', onLoadWindowResize, false);

    function onLoadWindowResize() {
        if (settingsData.projectLoaded && settingsData.flyIsComplete) {
            window.removeEventListener('resize', onLoadWindowResize, false);
            window.removeEventListener('orientationchange', onLoadWindowResize, false);
            return;
        }

        settingsData.isLandscape = window.matchMedia("(orientation: landscape)").matches;
        threeJsVariables.perspectiveCamera.aspect = window.innerWidth / window.innerHeight;
        threeJsVariables.perspectiveCamera.updateProjectionMatrix();
        threeJsVariables.renderer.setSize(window.innerWidth,  window.innerHeight);
        projectMode.LiteMode.resizeContainer();
    }
}

animate();

export async function startProject(settings = config['crmProjectSettings']) {
    // headerInit(settings);
    console.log('Start app');

    getVars(settings);
    getLordIconColors();
    debugOrbitControls();

    setInitialHtmlDirection(settings);
    loadCylinderGrid();
    // setTimeout(() => {
    RaycastHandler(settings);
    rightNavigationBar();
    leftNavigationBar(settings);
    audioInit();
    menuInit(settings);
    // backgroundSoundInit();
    langSwitcher(settings);
    const bottomNavBar = bottomNavBarInit();
    compassInit(bottomNavBar);
    zoomButtonsControl(bottomNavBar);
    // searchOpenButton();
    // }, settingsData.firstCameraFlyDuration);

    addLabelRenderContainer();
    widgetsInit(settings);
    chatsInit();
    projectMode.LiteMode?.runProjectStartAdditionalFunctions();
    initForm(document.body);
    cameraTour();
    initPoiData([
        {
            data: config.poiData
        },
        {
            data: config.poiCategories
        },
        {
            data: config.localization
        }
    ]);
    // !isMobile && await initPoiData();
    // !isMobile && setTimeout(() => createStreetNames(), 0);
    !isMobile && createStreetNames();
    addPoweredLogo();
    buildingHoverInit();
    getContactsDataFromAPI();
    new Weather(document.body);

    if (!isMobile && !isDesktopMode() && Buildings.getBuildingsInfoLength() >= 2) {
        new Tooltip();
        Tooltip.addTarget('.search__result-item');
    }

    /**
     *  Disable zoom on DOM elements
     */

    let touchDist2;

    document.body.addEventListener('touchmove', (e) => {

        if (e.touches.length >= 2) {
            const dx = e.touches[0].screenX - e.touches[1].screenX;
            const dy = e.touches[0].screenY - e.touches[1].screenY;

            touchDist2 = Math.sqrt((dx*dx)+(dy*dy));

            if (touchDist2 > 70 ){
                e.preventDefault();
            } else {
                return true;
            }
        }

    }, {passive: false});

    window.addEventListener('resize', onWindowResize, false);
    // window.addEventListener('dblclick', doubleClickHandler, false);

    if (getUrlParam('prop')) {
        createModal();
    }
    isTablet && !settingsData.isLandscape && lockScreenForTablet();
    autoRotateInit();
    await getGoogleAnalyticsData();
}

// const doubleClickHandler = (e) => {
//     e.preventDefault();
//     // console.log('window: ', settingsData.doubleClick)
//     settingsData.doubleClick = true;
//     setTimeout(() => {settingsData.doubleClick = false}, 1000)
// }


export function renderUpdateValuesOnDemand(value = false) {
    if (settingsData.stopRender !== value) {
        settingsData.stopRender = value;

        if (value) {
            console.warn('-=RENDER STOPPED=-');
        } else {
            console.warn('-=RENDER STARTED=-');
            settingsData.last_interaction = Date.now();
        }
    }
}

let size = new Vector2();

async function onWindowResize() {
    if (settingsData.stopWindowResizeEvents)
        return;

    const unitCard = UnitCardClass.findCard()?.html;

    await renderUpdateValuesOnDemand(false);
    await fitModelInViewport(true);
    // resizeHeader();
    buildingsLabelResize();
    // settingsData.activeViewMode !== ViewMode.Orbit && !isMobile
    //     ? searchResizeCanvas()
    //     : canvasResize();

    rightNavMenuZIndexChange();
    compassPositionChange();

    //postProcessingDoF2Resize();
    checkAndSetScreenOrientation();
    additionalMenuToggle();
    setWidthOfApplyNow();
    resizeMenu();

    setTimeout(() => {
        const tableLockContainer = document.querySelector('.tablet-plug');

        if (isTablet && tableLockContainer && settingsData.isLandscape) {
            tableLockContainer.remove();
        } else if (isTablet && !settingsData.isLandscape) {
            lockScreenForTablet();
        }
        // canvasResize();
        // updateLabelsRenderer();
        // setSizesToFloorSlider();
        setZoomOnWindowResize();
        moveElements();
        setMinAndMaxControlsDistance();

        if (document.querySelector('.search__container-main') && isMobile && !document.querySelector('.contact-us')) {
            removeSearchContainer('immediately');
            !unitCard && openSearchHandler();
        }

        // if (settingsData.activeViewMode === ViewMode.Orbit && isMobile) {
        //     canvasResize();
        // }

        resizeOrbitPoiCard();
        fitModelInViewport(true);
        canvasResize().then(() => panoramaOrbitResize());
        poiOrientationHandler();
    }, 100);


    const optionsCard = document.querySelector(`.${CardTypes.Options}-card`);

    if (optionsCard) {
        if (isMobile) {
            reopenDataBlockAfterResize();
        } else {
            setWidthOfCardDependingOnMenu();
        }
    }

    if (unitCard) {
        if (isMobile) {
            const cardId = unitCard.getAttribute('data-engineid');
            UnitCardClass.closeMobileCard();
            const {selectedMesh} = findSelectionAndCrmData(cardId);
            flatClick(selectedMesh);
            selectionBoxesSetStatusColor(selectedMesh, 'click');
            selectedMesh.userData.isClicked = true;
        }
    }

    const compareCards = document.querySelector(`.compare-block`);

    if (compareCards)
        setWidthOfCardDependingOnMenu(compareCards);

    threeJsVariables.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    threeJsVariables.renderer.getSize(size);

    threeJsVariables.composer?.setPixelRatio(Math.min(window.devicePixelRatio,2));
    threeJsVariables.composer?.setSize(size.x,size.y);
}

function checkAndSetScreenOrientation() {
    // settingsData.isLandscape = window.innerWidth <= 926 && window.innerHeight <= 450;
    // settingsData.isTabletPortrait = checkTabletMode();
    settingsData.isLandscape = window.matchMedia("(orientation: landscape)").matches;
    // settingsData.isTabletPortrait = checkTabletMode();

}

export function checkTabletMode () {
    // const correctViewPort = window.innerWidth  >= 768 && window.innerWidth  <= 1024 && window.innerHeight >= 850 && window.innerHeight <= 1366;
    // const correctUserAgent = navigator.userAgent.match(/Tablet|iPad/i);
    //
    // return correctUserAgent && correctViewPort;

    return false;
}

export function canvasResize() {
    const size = calculateCanvasSize();

    const vector = new Vector2(size.x, size.y);

    const canvasCurrentSize = threeJsVariables.renderer.getSize(new Vector2());
    const canvasBox = document.querySelector('.canvas-box');

    const time = (settingsData.isLandscape) ? 0.25 : 0.35;

    const side = getCurrentLangDirection() === 'ltr' ? 'left' : 'right';
    const desktop = isDesktopMode();


    return new Promise(resolve => {
        gsap.to(canvasCurrentSize, {
            duration: time,
            x: vector.x,
            y: vector.y,

            onUpdate: () => {
                if (!isMobile && !desktop) {
                    const diff = {
                        x: window.innerWidth - canvasCurrentSize.x,
                        y: window.innerHeight - canvasCurrentSize.y
                    };

                    canvasBox.style[side] = diff.x + 'px';
                }

                updateComponentsOnCanvasResize(canvasCurrentSize);
                projectMode.LiteMode.resizeContainer();
                PostFX.resize();
            },
            onComplete: () => {
                updateLabels();
                // setSizesToFloorSlider();
                showOrHideCylinderFloorNumbers();
                projectMode.FloorPlan.floorSlider?.resize();
                resolve(true);
            },
        });
    });
}

function calculateCanvasSize () {
    // const direction = getCurrentLangDirection();
    const searchContainer = document.querySelector('.search__container-main');
    const sideBarContainer = document.querySelector('.aside__container');

    const isSearchContainerMinimized = searchContainer?.classList.contains('minimize') || false;
    const isSearchContainerOpenFull = searchContainer?.classList.contains('open-full') || false;

    let searchWidth, searchHeight = 0;
    let menuWidth, menuHeight = 0;
    let cardWidth, cardHeight = 0;

    const size = {
        x: window.innerWidth,
        y: window.innerHeight
    };

    if (isMobile) {

        const card = UnitCardClass.findCard()?.html;

        if (settingsData.isLandscape) {
            searchWidth = isSearchContainerMinimized ? 0 : searchContainer ? searchContainer.getBoundingClientRect().width : 0;
            cardWidth = card ? card.getBoundingClientRect().width : 0;

            size.x = window.innerWidth - (searchWidth + cardWidth);

        } else {
            searchHeight = isSearchContainerMinimized ? 0 : isSearchContainerOpenFull && searchContainer ? searchContainer.getBoundingClientRect().height + 30 : searchContainer ? searchContainer.getBoundingClientRect().height - 30 : 0;
            cardHeight = card ? card.getBoundingClientRect().height : 0;

            size.y = window.innerHeight - (searchHeight + cardHeight);
        }

    } else {
        if (isDesktopMode()) {
            menuWidth = 0;
            searchWidth = searchContainer ? searchContainer.getBoundingClientRect().width : 0;

            // size.x = direction === 'ltr' ? window.innerWidth - (searchWidth + menuWidth) : window.innerWidth + (searchWidth + menuWidth);
            size.x = window.innerWidth - (searchWidth + menuWidth);
        } else {
            menuWidth = sideBarContainer.getBoundingClientRect().width;
            searchWidth = searchContainer ? searchContainer.getBoundingClientRect().width : 0;

            size.x = window.innerWidth - (searchWidth + menuWidth);
        }
    }
    return size;
}

function updateControls () {
    if (cameraAnimationActive())
        return;

    const orbitControlsTarget = controls.target;
    controls.update();
    trackballControls.target.set(orbitControlsTarget.x,orbitControlsTarget.y,orbitControlsTarget.z);
    trackballControls.update();
}

function addDocumentProperties () {
    const {projectTitle,cdnRepository,projectFolder} = config;

    document.title = projectTitle;

    const favicon = createHtmlElement(document.head,'','link',[]);
    favicon.setAttribute('rel','shortcut icon');
    favicon.setAttribute('type','image/png');
    favicon.href = `${cdnRepository}/${projectFolder}/resources/img/favicon.png`;
}

let fps_last_check = performance.now();
let fps_average = 0;
let fps_check_count = 0;
let last_time = 0;
let last_time_check = 0;
let fps_30 = 1000/30; //
let fps_60 = 1000/60; //
let max_fps = fps_30; // ограничение кадров в секунду, можно для мобилок сделать 30fps

// set errorTarget before flyIsComplete.
// flag is using to prevent infinite setting and rendering
//
// Alexeii
let isErrorTargetSet = false;
let originErrorTarget = null;
if (!isMobile) {
    originErrorTarget = threeJsVariables.tilesRenderer?.errorTarget;
    if (threeJsVariables.tilesRenderer?.hasOwnProperty('initialErrorTarget')) {
        threeJsVariables.tilesRenderer.errorTarget = threeJsVariables.tilesRenderer.initialErrorTarget;
    } else {
        isErrorTargetSet = true;
    }
}

function animate() {
    let now = performance.now();
    let delta = now - last_time;
    elapsedTime = clock.getElapsedTime();

    // проверяем частоту кадров
    fps_average = fps_average + (now - last_time_check);
    fps_check_count = fps_check_count + 1;
    last_time_check = now;

    settingsData.renderAnim = requestAnimationFrame(animate);

    if (delta < max_fps) {
        return;
    } else {
        if ((now - fps_last_check)>1000.0){
            fps_last_check = now;
            fps_average = fps_average / fps_check_count;
            if (fps_average<fps_30) {
                max_fps = fps_60
                //console.log('switch to 60fps')
            } else {
                max_fps = fps_30
                //console.log('switch to 30fps')
            }
            fps_average = 0;
            fps_check_count = 0
        }
    }
    //----------------------

    last_time = now;

    // if ( !loaderLineProgress() ){
    //     return
    // }

    // setting errorTarget to origin value after camera fly is completed
    if (settingsData.flyIsComplete && !isErrorTargetSet && !isMobile) {
        threeJsVariables.tilesRenderer.errorTarget = originErrorTarget;
        settingsData.needsRerender = true;
        isErrorTargetSet = true;
    }

    // console.log(threeJsVariables.mouse.x,threeJsVariables.mouse.y);
    TWEEN.update();
    updateControls();
    if (settingsData.slideShowed)
        cameraUpdate(delta);

    if (threeJsVariables.background){
        if (settingsData.activeViewMode === ViewMode.Cylinder || projectMode.LiteMode.animateSky){
            let a = threeJsVariables.background;
            a.rotation.y = a.rotation.y - 0.000005 * delta;
        }
    }
  

    if (!settingsData.stopRender && ((Date.now() - settingsData.last_interaction) >= settingsData.stopTimeRender)) {
        renderUpdateValuesOnDemand(true);
    }

    if (settingsData.needsRerender && settingsData.activeViewMode === ViewMode.Orbit && threeJsVariables.tilesRenderer) {
        renderLoop();
    }

    // not stop renderer
    // threeJsVariables.renderer.render(threeJsVariables.scene, threeJsVariables.perspectiveCamera);

    if (!settingsData.stopRender) {
        if (settingsData.postProcessing.isActive) {
            (threeJsVariables.composer !== null)
                ? threeJsVariables.composer.render()
                :  updateDoF2();
        } else if (settingsData.slideShowed) {
            if (PostFX.isActive()){
                PostFX.render(threeJsVariables.scene,threeJsVariables.perspectiveCamera,() => projectMode.LiteMode.renderTargetLayer());
            } else {
                threeJsVariables.renderer.render(threeJsVariables.scene, threeJsVariables.perspectiveCamera);
                cameraRender(delta, threeJsVariables.renderer, threeJsVariables.scene, threeJsVariables.perspectiveCamera);
                projectMode.LiteMode.renderTargetLayer();
            }
            updateLabelsRenderer();
            projectMode.LiteMode.updateTime(delta);
            Weather.render(elapsedTime);
        }

        // not stop renderer
        // updateRenderer();
        // waitAnimations();

        threeJsVariables.animator && settingsData.animateBuildingHover && threeJsVariables.animator.animate();

        if (threeJsVariables.stats !== null && isDev)
            threeJsVariables.stats.update();

    } else {
        settingsData.isIntersection = false;
        autoRotateTimerAction('cancel');
    }
    flatLabelResize();
    updateCameraTour();

}

// export function rendererAnimationPause(value) {
//     return;
//     if (value) {
//         setTimeout(() => {
//             settingsData.stopRender = value;
//         }, 1000);
//     } else {
//         settingsData.stopRender = value;
//     }
// }

export function updateRenderer() {
    //threeJsVariables.renderer.render(threeJsVariables.scene, threeJsVariables.perspectiveCamera); // not stop renderer
}

export function updateRaycaster() {
    if (projectMode.LiteMode.preventPointerEvents()) return;

    threeJsVariables.raycaster.setFromCamera(threeJsVariables.mouse, threeJsVariables.perspectiveCamera);
    checkIntersection();
}

export function updateLabelsRenderer() {
    if (Object.keys(threeJsVariables.labelRenderer).length) {
        threeJsVariables.labelRenderer.render(threeJsVariables.scene, threeJsVariables.perspectiveCamera);
    }
}

export function getCanvasWidthDiff() {
    const {innerWidth} = window;
    const canvas = threeJsVariables.renderer.getSize(new Vector2());

    if (isMobile){
        if (settingsData.isLandscape && getCurrentLangDirection() === 'ltr') {
            return  new Vector2(0, 0);

        } else if (settingsData.isLandscape && getCurrentLangDirection() === 'rtl') {
            return new Vector2(innerWidth - canvas.x, canvas.y);
        } else {
            return new Vector2(0, 0);
        }
    } else if (isDesktopMode()) {
        return new Vector2(0,0);
    } else {
        if (getCurrentLangDirection() === 'ltr') {
            return new Vector2(innerWidth - canvas.x, canvas.y);
        } else {
            return new Vector2(0, 0);
        }
    }
}

export function updateLabels() {
    const canvasSize = threeJsVariables.renderer.getSize(new Vector2());
    if (Object.keys(threeJsVariables.labelRenderer).length) {
        threeJsVariables.labelRenderer.setSize(canvasSize.x, canvasSize.y);
        const langDirection = getCurrentLangDirection();
        switch (langDirection) {
            case 'rtl':
                threeJsVariables.labelRenderer.domElement.style.right = '0';
                break;
            case 'ltr':
                threeJsVariables.labelRenderer.domElement.style.left = '0';
                break;
        }
    }
}

// function waitAnimations () {
//     if (settingsData.animationsList.length === 0){
//         return;
//     }
//
//     userPrevent(true);
//
//     settingsData.activeViewMode === ViewMode.FloorPlan && flatLabelsRemove();
//
//     Promise.all(settingsData.animationsList)
//         .then(()=>{
//             settingsData.animationsList = [];
//             userPrevent(false);
//             settingsData.activeViewMode === ViewMode.FloorPlan && flatLabelsUpdate(settingsData.currentFloor);
//         })
// }

function addLabelRenderContainer() {
    const threeCSS = document.createElement('div');
    threeCSS.setAttribute('id', 'three_d_css');
    document.querySelector('.canvas-box').appendChild(threeCSS);

    threeJsVariables.labelRenderer = new CSS2DRenderer();
    threeJsVariables.labelRenderer.setSize(window.innerWidth, window.innerHeight);
    // threeJsVariables.labelRenderer.domElement.style.position = 'absolute';
    // threeJsVariables.labelRenderer.domElement.style.top = '0';
    // threeJsVariables.labelRenderer.domElement.style.pointerEvents = 'none';
    threeCSS.appendChild(threeJsVariables.labelRenderer.domElement);
    threeJsVariables.labelRenderer.domElement.addEventListener('click', (e)=>{
        cssPoiHandler(e);
    });

    return threeJsVariables.labelRenderer;
}


// const cursorOffset = {
//     left : 20,
//     top  : -15
// };
//
// const cursor = document.getElementById('cursor');
//
// document.getElementById('c').addEventListener('mousemove',function(e) {
//     if (settingsData.customCursor){
//         e.target.style.cursor = 'none';
//         cursor.style.left = (e.pageX - cursorOffset.left) + 'px';
//         cursor.style.top = (e.pageY - cursorOffset.top) + 'px';
//         cursor.style.display = 'block';
//     } else {
//         cursor.style.display = 'none';
//     }
// }, false);